import {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { KeenIcon } from '@/components';
import clsx from 'clsx';
import { Tooltip } from 'react-tooltip';
import { useIntl } from 'react-intl';
import MediaUploadModal from './MediaUploadModal';
import { ImageCaptionDTO } from '@/types/api.types';
import ImageCropperModal from '@/components/ImageCropperModal';

interface MediaUploadProps<T> {
  field: string;
  media: File | string | null | undefined | (File | string)[];
  setFormData: Dispatch<SetStateAction<Partial<T>>>;
  errors: { [key: string]: string };
  setErrors: (errors: { [key: string]: string }) => void;
  disabled: boolean;
  allowedTypes: string[];
  maxFiles: number;
  previewMode: boolean;
  media_captions?: ImageCaptionDTO[];
  required?: boolean;
  guidelinesResolution?: string;
  onMediaRemoved?: (removedMedia: string[]) => void;
  closeButton?: boolean;
}

const MediaUpload = <T,>({
  field,
  media,
  setFormData,
  errors,
  setErrors,
  disabled,
  allowedTypes,
  maxFiles,
  previewMode,
  media_captions,
  required,
  guidelinesResolution,
  onMediaRemoved,
  closeButton = true,
}: MediaUploadProps<T>) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const intl = useIntl();
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const COVER_IMAGE_ASPECT = useMemo(() => 21 / 9, []);
  const MIN_CROP_WIDTH = 1200;
  const MIN_CROP_HEIGHT = 500;

  const [isCropModalOpen, setIsCropModalOpen] = useState(false);
  const [pendingCropFile, setPendingCropFile] = useState<File | null>(null);
  const [pendingCaptions, setPendingCaptions] = useState<ImageCaptionDTO[]>([]);
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);
  useEffect(() => {
    const files = Array.isArray(media) ? media : media ? [media] : [];

    const urls = files.map((item: File | string) => {
      if (item instanceof File) {
        return URL.createObjectURL(item);
      }
      return typeof item === 'string' ? item : '';
    });

    setPreviewUrls(urls);

    return () => {
      urls.forEach((url: string) => {
        if (url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, [media]);

  const getCurrentMediaArray = useCallback(() => {
    return Array.isArray(media) ? media : media ? [media] : [];
  }, [media]);

  const setFieldError = useCallback(
    (message: string) => {
      setErrors({
        ...errors,
        [field]: message,
      });
    },
    [errors, field, setErrors]
  );

  const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    setLoading(true);
    // Snapshot the FileList into an array BEFORE clearing the input,
    // because clearing input.value can invalidate the FileList reference
    // in some browsers, causing silent empty-file failures for logo.
    const fileArray = Array.from(files);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    const validFiles = fileArray.filter((file) =>
      allowedTypes.includes(file.type)
    );
    const hasInvalidType = validFiles.length !== fileArray.length;

    if (hasInvalidType) {
      setFieldError(
        intl.formatMessage(
          { id: 'VALIDATION.INVALID_FILE_FORMAT' },
          {
            types: allowedTypes
              .map((type) => type.split('/')[1].toUpperCase())
              .join(', '),
          }
        )
      );
      setLoading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    if (validFiles.length === 0) {
      setLoading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    // Clear any previous error once we have valid file(s)
    setFieldError('');

    if (field === 'logo') {
      const selectedFile = validFiles[0] || null;
      setFormData((prev) => ({
        ...prev,
        [field]: selectedFile,
      }));
    } else {
      const currentFiles = getCurrentMediaArray();
      if (currentFiles.length + validFiles.length > maxFiles) {
        setFieldError(
          intl.formatMessage(
            { id: 'VALIDATION.MAX_FILES' },
            { count: maxFiles }
          ) || `Maximum ${maxFiles} images allowed`
        );
        setLoading(false);
        return;
      }

      const newFiles = [...currentFiles, ...validFiles];
      setFormData((prev) => ({
        ...prev,
        [field]: maxFiles === 1 ? newFiles[0] || null : newFiles,
      }));
    }

    setLoading(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleModalSave = (
    updatedMedia: (File | string)[],
    updatedCaptions: ImageCaptionDTO[]
  ) => {
    if (onMediaRemoved) {
      const before = getCurrentMediaArray();
      const removedExisting = before
        .filter((item) => typeof item === 'string')
        .filter((url) => !updatedMedia.includes(url));

      if (removedExisting.length > 0) {
        onMediaRemoved(removedExisting);
      }
    }

    const captionField =
      field === 'media' || field === 'images'
        ? 'media_captions'
        : `${field}_captions`;

    if (
      field === 'coverImage' &&
      maxFiles === 1 &&
      updatedMedia[0] instanceof File
    ) {
      setPendingCropFile(updatedMedia[0]);
      setPendingCaptions(updatedCaptions);
      setIsModalOpen(false);
      setIsCropModalOpen(true);
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [field]: maxFiles === 1 ? updatedMedia[0] || null : updatedMedia,
      [captionField]: updatedCaptions,
    }));
    setFieldError('');
  };

  const handleCropClose = useCallback(() => {
    setIsCropModalOpen(false);
    setPendingCropFile(null);
    setPendingCaptions([]);
  }, []);

  const handleCropConfirm = useCallback(
    (croppedFile: File) => {
      const captionField =
        field === 'media' || field === 'images'
          ? 'media_captions'
          : `${field}_captions`;

      setFormData((prev) => ({
        ...prev,
        [field]: croppedFile,
        [captionField]: pendingCaptions,
      }));
      setFieldError('');
      handleCropClose();
    },
    [field, handleCropClose, pendingCaptions, setFieldError, setFormData]
  );

  const handleRemoveFile = (index?: number) => {
    setLoading(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    if (field === 'logo') {
      setFormData((prev) => ({
        ...prev,
        [field]: null,
      }));
      setFieldError('');
    } else {
      const currentFiles = getCurrentMediaArray();

      if (
        onMediaRemoved &&
        index !== undefined &&
        typeof currentFiles[index] === 'string'
      ) {
        onMediaRemoved([currentFiles[index] as string]);
      }

      const updatedFiles =
        index !== undefined
          ? currentFiles.filter((_: File | string, i: number) => i !== index)
          : [];

      // Also remove caption if it exists
      let updatedCaptions = media_captions;
      const captionField =
        field === 'media' || field === 'images'
          ? 'media_captions'
          : `${field}_captions`;

      if (index !== undefined && media_captions) {
        updatedCaptions = media_captions
          .filter((c) => c.image_index !== index)
          .map((c) => ({
            ...c,
            image_index:
              (c.image_index ?? 0) > index
                ? (c.image_index ?? 0) - 1
                : c.image_index,
          }));
      }

      setFormData((prev) => ({
        ...prev,
        [field]: maxFiles === 1 ? updatedFiles[0] || null : updatedFiles,
        ...(media_captions ? { [captionField]: updatedCaptions } : {}),
      }));
      setFieldError('');
    }
  };

  const renderPreviews = () => {
    if (loading || previewUrls.length === 0) {
      return null;
    }

    return (
      <div className="flex flex-wrap gap-2">
        {previewUrls.map((src, index) => {
          if (!src) return null;

          const files = Array.isArray(media) ? media : media ? [media] : [];
          const item = files[index];
          const isVideo =
            (item instanceof File && item.type.startsWith('video/')) ||
            (typeof item === 'string' && item.endsWith('.mp4'));

          return (
            <div key={index} className="relative w-24 h-24">
              {isVideo ? (
                <video
                  src={src}
                  className="w-full h-full object-cover rounded-lg"
                  controls
                  muted
                  loop
                  aria-label={`Video preview ${index + 1}`}
                >
                  Your browser does not support the video tag.
                </video>
              ) : (
                <img
                  src={src}
                  alt={
                    field === 'logo'
                      ? 'Logo preview'
                      : intl.formatMessage(
                          { id: 'HARBOR.FORM.MEDIA_PREVIEW_ALT' },
                          { index: index + 1 }
                        )
                  }
                  className="w-full h-full object-cover rounded-lg"
                />
              )}
              {field !== 'logo' &&
                media_captions?.find((c) => c.image_index === index) && (
                  <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-[10px] px-1 py-0.5 rounded-b-lg truncate">
                    {intl.formatMessage({ id: 'COMMON.HAS_CAPTIONS' })}
                  </div>
                )}
              {!disabled && closeButton && (
                <button
                  type="button"
                  onClick={() =>
                    handleRemoveFile(field === 'logo' ? undefined : index)
                  }
                  className="absolute top-1 right-1 bg-red-500 contrast:bg-red-800 text-white rounded-full w-6 h-6 flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-red-500"
                  aria-label={
                    field === 'logo'
                      ? 'Remove logo'
                      : intl.formatMessage(
                          { id: 'HARBOR.FORM.REMOVE_MEDIA_INDEX' },
                          { index: index + 1 }
                        )
                  }
                >
                  <KeenIcon type="filled" icon="cross" className="text-sm" />
                </button>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderLoader = () => {
    if (!loading) return null;
    return (
      <div className="relative w-24 h-24 bg-gray-100 rounded-lg flex items-center justify-center">
        <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  };

  return (
    <div className="mb-6">
      <label
        htmlFor={`${field}-input`}
        className="block text-sm font-medium text-gray-700 mb-2 contrast:!text-white"
      >
        {field === 'logo'
          ? intl.formatMessage({ id: 'HARBOR.FORM.LOGO' })
          : field === 'fairway_map'
            ? intl.formatMessage({ id: 'HARBOR.FORM.FAIRWAY_MAP' })
            : field === 'fairway_entrance_map'
              ? intl.formatMessage({ id: 'HARBOR.FORM.FAIRWAY_ENTRANCE_MAP' })
              : field === 'berth_image'
                ? intl.formatMessage({ id: 'HARBOR.FORM.BERTH_IMAGE' })
                : field === 'media' || field === 'images' || field === 'photos'
                  ? intl.formatMessage({ id: 'HARBOR.FORM.PHOTOS_VIDEOS' })
                  : field === 'coverImage'
                    ? intl.formatMessage({
                        id: 'THEME_ROUTES.FORM.COVER_IMAGE.LABEL',
                        defaultMessage: 'Cover image',
                      })
                    : ''}
        {required && <span className="text-red-500 ml-1">*</span>}
        <span
          data-tooltip-id="imageTooltip"
          data-tooltip-content={
            media
              ? intl.formatMessage({
                  id: 'HARBOR.FORM.ALLOWED_FORMATS_WITH_VIDEO',
                })
              : intl.formatMessage({
                  id: 'HARBOR.FORM.ALLOWED_FORMATS_IMAGE_ONLY',
                })
          }
          className="ml-1 inline-flex"
        >
          <KeenIcon
            type="filled"
            icon="information"
            className="text-gray-500 cursor-pointer contrast:!text-white"
          />
        </span>
        <Tooltip id="imageTooltip" place="top" className="!max-w-xs" />
      </label>

      <input
        id={`${field}-input`}
        ref={fileInputRef}
        type="file"
        multiple={field === 'fairway_map' || field === 'fairway_entrance_map'}
        accept={allowedTypes.join(',')}
        onChange={handleFileChange}
        disabled={disabled || loading}
        aria-label={`Upload ${field}`}
        data-testid={`${field}-input`}
        className="sr-only"
      />

      {/* Show preview above button if previewMode is true */}
      {previewMode && (
        <div className="flex flex-wrap gap-2 mb-2">
          {renderPreviews()}
          {renderLoader()}
        </div>
      )}
      {!disabled && (
        <div className="relative inline-block">
          {/* Custom button */}
          <button
            type="button"
            onClick={() => {
              if (
                field === 'media' ||
                field === 'images' ||
                field === 'photos' ||
                field === 'fairway_map' ||
                field === 'fairway_entrance_map' ||
                field === 'berth_image' ||
                field === 'coverImage'
              ) {
                setIsModalOpen(true);
              } else {
                fileInputRef.current?.click();
              }
            }}
            disabled={disabled || loading}
            className={clsx(
              'py-2 px-4 rounded-lg border-0 text-sm font-semibold bg-blue-50 dark:bg-blue-900 contrast:bg-blue-900 text-blue-700 dark:text-blue-200 contrast:text-blue-100 hover:bg-blue-100 dark:hover:bg-blue-800 contrast:hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500',
              (disabled || loading) && 'opacity-50 cursor-not-allowed'
            )}
          >
            {field === 'logo'
              ? intl.formatMessage({ id: 'HARBOR.FORM.UPLOAD_LOGO' })
              : intl.formatMessage({ id: 'HARBOR.FORM.UPLOAD_MEDIA' })}
          </button>
          {(field === 'media' ||
            field === 'images' ||
            field === 'photos' ||
            field === 'fairway_map' ||
            field === 'fairway_entrance_map' ||
            field === 'berth_image' ||
            field === 'coverImage') && (
            <>
              <MediaUploadModal
                open={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSave={handleModalSave}
                allowedTypes={allowedTypes}
                maxFiles={maxFiles}
                initialMedia={
                  Array.isArray(media) ? media : media ? [media] : []
                }
                initialCaptions={media_captions || []}
                guidelinesResolution={guidelinesResolution}
              />
            </>
          )}

          {field === 'coverImage' && (
            <ImageCropperModal
              open={isCropModalOpen}
              file={pendingCropFile}
              aspect={COVER_IMAGE_ASPECT}
              minWidth={MIN_CROP_WIDTH}
              minHeight={MIN_CROP_HEIGHT}
              title="Crop cover image"
              onClose={handleCropClose}
              onConfirm={handleCropConfirm}
            />
          )}
          {errors[field] && (
            <p
              id={`${field}-error`}
              className="text-red-500 text-xs mt-1"
              role="alert"
            >
              {errors[field]}
            </p>
          )}
        </div>
      )}
      {/* Show preview below button if previewMode is false */}
      {!previewMode && (
        <div className="flex flex-wrap gap-2 mt-2">
          {renderPreviews()}
          {renderLoader()}
        </div>
      )}
    </div>
  );
};

export default MediaUpload;
